import React, { useState, useEffect, useCallback } from 'react';
import { format, subDays } from 'date-fns';
import ReportLayout, { ReportTable, usePagination } from './ReportLayout';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import clsx from 'clsx';

const statusBadge = (v, map) => {
  const cls = map[v] || 'bg-slate-100 text-slate-600';
  return <span className={clsx('px-2 py-0.5 rounded-full text-xs font-medium capitalize', cls)}>{v?.replace('_',' ') || '–'}</span>;
};

const REGO_MAP = { valid:'bg-green-100 text-green-700', expiring_soon:'bg-amber-100 text-amber-700', expired:'bg-red-100 text-red-700', unknown:'bg-slate-100 text-slate-500' };
const SHIFT_MAP = { booked:'bg-blue-100 text-blue-700', completed:'bg-green-100 text-green-700', cancelled:'bg-red-100 text-red-700', pending:'bg-amber-100 text-amber-700' };

const COLS = [
  { key: 'registration',    label: 'Rego' },
  { key: 'make',            label: 'Make' },
  { key: 'model',           label: 'Model' },
  { key: 'color',           label: 'Color' },
  { key: 'vehicle_status',  label: 'Fleet Status', render: v => statusBadge(v, { available:'bg-green-100 text-green-700', in_use:'bg-blue-100 text-blue-700', maintenance:'bg-amber-100 text-amber-700', retired:'bg-slate-100 text-slate-500' }) },
  { key: 'rego_status',     label: 'Rego Status',  render: v => statusBadge(v, REGO_MAP) },
  { key: 'shift_date',      label: 'Shift Date' },
  { key: 'staff_name',      label: 'Staff Member' },
  { key: 'client_names',    label: 'Client(s)' },
  { key: 'shift_type',      label: 'Shift Type' },
  { key: 'start_time',      label: 'Start' },
  { key: 'end_time',        label: 'End' },
  { key: 'km',              label: 'KM',           render: v => v ? `${parseFloat(v).toFixed(1)} km` : '–' },
  { key: 'shift_status',    label: 'Shift Status', render: v => statusBadge(v, SHIFT_MAP) },
  { key: 'address',         label: 'Address' },
];

export default function FleetUsageReport() {
  const [data, setData]           = useState([]);
  const [vehicles, setVehicles]   = useState([]);
  const [staff, setStaff]         = useState([]);
  const [loading, setLoading]     = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [startDate, setStart]     = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [endDate, setEnd]         = useState(format(new Date(), 'yyyy-MM-dd'));
  const [vehicleId, setVehicleId] = useState('');
  const [staffId, setStaffId]     = useState('');
  const pagination = usePagination(data);

  useEffect(() => {
    api.get('/vehicles').then(r => setVehicles(r.data || [])).catch(() => {});
    api.get('/staff').then(r => setStaff(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams({ start_date: startDate, end_date: endDate });
    if (vehicleId) p.set('vehicle_id', vehicleId);
    if (staffId)   p.set('staff_id', staffId);
    api.get(`/reports/fleet-usage?${p}`)
      .then(r => { setData(r.data || []); pagination.reset(); })
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  }, [startDate, endDate, vehicleId, staffId]);

  useEffect(() => { load(); }, [load]);

  const totalKm = data.reduce((s, r) => s + parseFloat(r.km || 0), 0);
  const shiftsWithVehicle = data.filter(r => r.shift_id).length;

  const selVehicle = vehicles.find(v => v.id === vehicleId);
  const selStaff   = staff.find(s => s.id === staffId);
  const chips = [
    { label: 'Date Range', value: `${startDate} — ${endDate}` },
    ...(selVehicle ? [{ label: 'Vehicle', value: selVehicle.registration, onRemove: () => setVehicleId('') }] : []),
    ...(selStaff   ? [{ label: 'Staff',   value: `${selStaff.first_name} ${selStaff.last_name}`, onRemove: () => setStaffId('') }] : []),
  ];

  return (
    <ReportLayout title="Fleet Usage Report" data={data} filename="fleet_usage_report" columns={COLS}
      activeChips={chips} showFiltersPanel={showFilters} onToggleFilters={() => setShowFilters(v => !v)}
      filters={
        <div className="flex flex-wrap gap-4">
          <div><label className="block text-xs font-semibold text-slate-500 mb-1">Start Date</label>
            <input type="date" value={startDate} onChange={e => setStart(e.target.value)} className="input-field !w-auto text-sm" /></div>
          <div><label className="block text-xs font-semibold text-slate-500 mb-1">End Date</label>
            <input type="date" value={endDate} onChange={e => setEnd(e.target.value)} className="input-field !w-auto text-sm" /></div>
          <div><label className="block text-xs font-semibold text-slate-500 mb-1">Vehicle</label>
            <select value={vehicleId} onChange={e => setVehicleId(e.target.value)} className="input-field !w-auto text-sm">
              <option value="">All Vehicles</option>
              {vehicles.map(v => <option key={v.id} value={v.id}>{v.registration}{v.make ? ` — ${v.make}` : ''}{v.model ? ` ${v.model}` : ''}</option>)}
            </select></div>
          <div><label className="block text-xs font-semibold text-slate-500 mb-1">Staff</label>
            <select value={staffId} onChange={e => setStaffId(e.target.value)} className="input-field !w-auto text-sm">
              <option value="">All Staff</option>
              {staff.map(s => <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>)}
            </select></div>
          <div className="flex items-end">
            <button onClick={load} className="btn-primary text-sm px-4 py-2">Apply</button>
          </div>
        </div>
      }>
      <div className="grid grid-cols-3 gap-4 mb-4">
        {[
          { label: 'Fleet Size',        value: vehicles.length },
          { label: 'Shifts with Vehicle', value: shiftsWithVehicle },
          { label: 'Total KM (period)', value: `${totalKm.toFixed(1)} km` },
        ].map(card => (
          <div key={card.label} className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="text-xs text-slate-500 mb-1">{card.label}</div>
            <div className="text-2xl font-bold text-slate-900">{card.value}</div>
          </div>
        ))}
      </div>
      <ReportTable columns={COLS} data={data} loading={loading} pagination={pagination}
        emptyMsg="No fleet assignments found for this period." />
    </ReportLayout>
  );
}
